import { css } from '@emotion/react';
import { Link, LinkProps } from './link';
import { ReactElement, Children, cloneElement } from 'react';
import { Button, ButtonProps } from './button';
import { getMediaDown, getMediaUp } from '../foundations/breakpoints';
import { getColor } from '../foundations/adg-colors';
import { Icon, IconProps } from '../foundations/icon';

type TableChildren =
  | typeof TableCell
  | typeof FeatureDivider
  | typeof TitleRow
  | typeof TableRow
  | typeof PricingCard;

interface DividerProps {
  children: string;
  colCount: number;
  isHeading?: boolean;
  isShaded?: boolean;
}

interface TableRowProps {
  columns?: string[];
  children: ReactElement<typeof TableCell> | ReactElement<typeof TableCell>[];
}

export interface TableProps {
  children: ReactElement<TableChildren> | ReactElement<TableChildren>[];
  columns: string[];
  alternatingRowColors?: boolean;
  collapse?: boolean;
}

interface TitleRowProps {
  columns?: string[];
  isShaded?: boolean;
  isSmall?: boolean;
  centerText?: boolean;
}

interface PricingCardProps {
  title: string;
  price: number;
  helpText: string;
  buttonProps: ButtonProps;
}

//TODO: replace iconProps with a real type when the icon component is done
interface TableCellProps {
  column?: string;
  isFirst?: boolean;
  centerText?: boolean;
  contentProps: CellContentProps;
  isShaded?: boolean;
  isInlineDivider?: boolean;
}

const tableStyles = (alternatingRowColors: boolean, collapse: boolean) => css`
  border-collapse: collapse;
  width: 100%;

  h3,
  h4 {
    margin: 0;
    font-size: inherit;
    font-weight: inherit;
  }
  p {
    margin: 0;

    &.cell-label-p {
      ${getMediaUp(collapse ? 'md' : 'xs')} {
        display: none;
      }
    }
  }

  tr {
    &.tr-body-row {
      ${getMediaDown(collapse ? 'md' : 'never')} {
        margin-bottom: 20px;
      }
    }

    ${
      alternatingRowColors
        ? `
    ${getMediaUp(collapse ? 'md' : 'xs')} {
      &:nth-child(even) td {
        background-color: ${getColor('n20')};
      }
    }
    `
        : ''
    }
  }

  td {
    &.feature-divider {
      background: ${getColor('n20')});
      border: 1px solid #dfe1e6;
      ${getMediaDown(collapse ? 'md' : 'never')} {
        text-align: left;
        padding: 15px;
      }
    }

    .cell-content-div {
      display: flex;
      flex-direction: row;
      justify-content: center;
      ${getMediaDown(collapse ? 'md' : 'never')} {
        justify-content: space-between;
        padding: 16px;
      }

      &.first {
        display: inline;
        text-align: left;
        ${getMediaDown(collapse ? 'md' : 'never')} {
          background: #fafbfc;
          display: block;
          font-size: 1rem;
          font-style: normal;
          font-weight: 600;
          line-height: 1.25rem; /* 125% */
          margin: auto;
          letter-spacing: 0.3px;
        }
      }
    }
  }
  th {
    ${getMediaDown(collapse ? 'md' : 'never')} {
      display: none;
    }
  }

  ${getMediaUp(collapse ? 'md' : 'xs')} {
    table-layout: fixed;
    th,
    td {
      display: table-cell;
      width: auto;
      padding: 15px;
      vertical-align: middle;
    }

    tr {
      display: table-row;
    }
  }

  ${getMediaDown(collapse ? 'md' : 'never')} {
    display: flex;
    flex-direction: column;

    tr {
      display: flex;
      flex-direction: column;
    }
  }
`;

type boollike = boolean | undefined;

const titleRowStyles = (
  shaded: boollike,
  centerText: boollike,
  isSmall: boollike
) => {
  return css`
    background: ${shaded ? getColor('n20') : getColor('n0')};
    box-shadow: -1px 0px 0px 0px #dfe1e6 inset, 1px 0px 0px 0px #dfe1e6 inset,
      0px -1px 0px 0px #dfe1e6 inset, 0px 1px 0px 0px #dfe1e6 inset;
    color: ${getColor('n900')};
    font-size: ${isSmall ? `1.25rem` : `1.5rem`};
    font-style: normal;
    font-weight: 600;
    line-height: ${isSmall ? '1.5rem' : '2rem'};
    letter-spacing: 0.3px;
    text-align: ${centerText ? 'center' : 'left'};
  `;
};

const dividerStyles = (shaded: boollike, isHeading: boollike) => css`
  background: ${shaded ? getColor('n20') : getColor('n0')};
  color: ${getColor('n900')};
  font-size: ${isHeading ? '1.25rem' : '1.125rem'};
  font-style: normal;
  font-weight: 600;
  line-height: ${isHeading ? '1.5rem' : '1.625rem'};
  letter-spacing: 0.3px;
  text-align: ${isHeading ? 'center' : 'left'};
`;

const tableCellStyles = (
  shaded: boollike,
  centerText: boollike,
  isInlineDivider: boollike
) => {
  return css`
    background: ${shaded || isInlineDivider ? getColor('n20') : getColor('n0')};
    box-shadow: -1px 0px 0px 0px #dfe1e6 inset, 1px 0px 0px 0px #dfe1e6 inset,
      0px -1px 0px 0px #dfe1e6 inset, 0px 1px 0px 0px #dfe1e6 inset;
    color: ${getColor('n900')};
    font-size: 1.125rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.625rem;
    letter-spacing: 0.3px;
    text-align: ${centerText ? 'center' : 'left'};
  `;
};

const priceTitleStyles = css`
  background: ${getColor('n0')};
  border: 1px solid #dfe1e6;
  color: ${getColor('n900')};
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: 2rem;
  letter-spacing: 0.3px;
  text-align: center;
`;

const priceHeadingStyles = css`
  color: ${getColor('n900')};
  font-size: 3.375rem;
  font-style: normal;
  font-weight: 700;
  line-height: 3.75rem;
  text-align: center;
`;

const priceHelpStyles = css`
  color: ${getColor('n900')};
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
`;

export const Table = ({
  children,
  columns,
  alternatingRowColors = false,
  collapse = true
}: TableProps) => {
  const injectProps: Partial<TableRowProps> = { columns };
  const renderChildren = () => {
    return Children.map(children, (child) => cloneElement(child, injectProps));
  };
  return (
    <table css={tableStyles(alternatingRowColors, collapse)}>
      {renderChildren()}
    </table>
  );
};

export const TableHeader = ({ children, colCount }: DividerProps) => (
  <tr className="tr-header-row">
    <td colSpan={colCount} css={titleRowStyles(true, true, false)}>
      <h3>{children}</h3>
    </td>
  </tr>
);

export const FeatureDivider = ({
  children,
  colCount,
  isHeading,
  isShaded
}: DividerProps) => (
  <tr className="tr-feature-divider-row">
    <td
      className="feature-divider"
      colSpan={colCount}
      css={[
        css`
          ${isHeading ? 'text-align: center;' : 'text-align: left;'}
        `,
        dividerStyles(isShaded, isHeading)
      ]}
    >
      {isHeading ? <h4>{children}</h4> : <p>{children}</p>}
    </td>
  </tr>
);

export const TitleRow = ({ columns, centerText, isSmall }: TitleRowProps) => {
  const mapColumns = columns || [];
  return (
    <thead className="title-row">
      <tr className="tr-title-row">
        {mapColumns.map((text: string, idx: number) => (
          <th css={[titleRowStyles(idx != 0, centerText, isSmall)]} key={text}>
            {isSmall ? <h4>{text}</h4> : <h3>{text}</h3>}
          </th>
        ))}
      </tr>
    </thead>
  );
};

export const TableRow = ({ children, columns }: TableRowProps) => {
  const mapColumns = columns || [];
  const renderChildren = (
    children: ReactElement<typeof TableCell> | ReactElement<typeof TableCell>[]
  ) => {
    return Children.map(children, (child, idx) => {
      const injectProps: Partial<TableCellProps> = {
        column: mapColumns[idx],
        isFirst: idx === 0
      };
      return cloneElement(child, injectProps);
    });
  };
  return <tr className="tr-body-row">{renderChildren(children)}</tr>;
};

interface CellIconProps {
  iconProps: IconProps;
}

interface CellTextProps {
  text: string;
  isUnderlined: boollike;
}

interface CellLinkProps {
  linkProps: LinkProps;
}

type CellContentProps = CellTextProps | CellIconProps | CellLinkProps;

const renderCellContent = (props: CellContentProps) => {
  if (!props) {
    return null;
  }
  if ('text' in props) {
    const { text, isUnderlined } = props;
    return (
      <p
        css={
          isUnderlined &&
          css`
            text-decoration-color: #a5adba;
            text-decoration-line: underline;
            text-decoration-style: dashed;
            text-underline-position: under;
          `
        }
      >
        {text}
      </p>
    );
  } else if ('linkProps' in props) {
    return <Link {...props.linkProps} />;
  } else if ('iconProps' in props) {
    return <Icon size="xs" {...props.iconProps} />;
  }
  return null;
};

export const TableCell = ({
  contentProps,
  column,
  isShaded,
  isInlineDivider,
  centerText,
  isFirst
}: TableCellProps) => {
  return (
    <td css={tableCellStyles(isShaded, centerText, isInlineDivider)}>
      <div className={`cell-content-div ${isFirst ? 'first' : ''}`}>
        {!isFirst && <p className="cell-label-p">{column}</p>}
        {renderCellContent(contentProps)}
      </div>
    </td>
  );
};

const pricingContainerStyles = css`
  border-collapse: collapse;
  border: 1px solid #dfe1e6;
`;

const pricingLayoutStyles = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const renderPricingCard = (props: PricingProps) => {
  if (!props) {
    return null;
  } else {
    const { title, price, helpText, buttonProps } = props;
    return (
      <div css={pricingLayoutStyles}>
        <div
          css={[
            priceTitleStyles,
            css`
              width: 100%;
            `
          ]}
        >
          <h3>{title}</h3>
        </div>
        <div css={priceHeadingStyles}>
          <h3>{`$${price}`}</h3>
        </div>
        <div css={priceHelpStyles}>
          <p>{helpText}</p>
        </div>
        <Button {...buttonProps} />
      </div>
    );
  }
};

type PricingProps = PricingCardProps | undefined;
export const PricingCard = (props: PricingProps) => {
  return <td css={pricingContainerStyles}>{renderPricingCard(props)}</td>;
};
